/**
 * Season Statistics Hook
 *
 * Fetches user's season-specific activity statistics from the backend.
 * Includes: token/NFT burns, chest opens, SOL rewards, and referrals.
 * Data is accurately calculated from transaction records for the season date range.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@/context/AuthContext';
import ENDPOINTS from '@/api';

export interface SeasonStats {
  // Junk (Token/NFT burning) stats
  junk: {
    numberOfTokens: number;
    recycledCredits: number;
    pointsRewards: number;
  };
  // Chest opening stats
  chest: {
    numberOfChestOpened: number;
    creditsRewarded: number;
    pointsRewards: number;
  };
  // Referral stats
  referral: {
    numberOfReferral: number;
    referralPointsRewarded: number;
    pointsRewards: number;
  };
  // Points lottery stats
  pointsLottery: {
    seasonPointsSpent: number;
    creditsRewards: number;
    seasonNFT: number;
  };
}

async function fetchSeasonStats(seasonId: number): Promise<SeasonStats> {
  try {
    console.log('[useSeasonStats] Fetching stats for season:', seasonId);

    const url = `${ENDPOINTS.pointsLotterySeasonActivity}/${seasonId}/user-activity`;
    const response = await fetch(url, {
      credentials: 'include',
    });

    const json = await response.json();

    if (!response.ok || json.code !== 0) {
      const message = json.message || 'Failed to fetch season activity';
      console.error('[useSeasonStats] Error:', message);
      throw new Error(message);
    }

    const data = json.data;

    console.log('[useSeasonStats] Season activity data:', data);

    // Transform backend response to match SeasonStats interface
    return {
      junk: {
        numberOfTokens: data.junk.numberOfTokens + data.junk.numberOfNFTs, // Total burns (tokens + NFTs)
        recycledCredits: data.junk.recycledCredits, // SOL earned from burns
        pointsRewards: data.junk.pointsRewards,
      },
      chest: {
        numberOfChestOpened: data.chest.numberOfChestOpened,
        creditsRewarded: data.chest.creditsRewarded, // SOL earned from chests
        pointsRewards: data.chest.pointsRewards,
      },
      referral: {
        numberOfReferral: data.referral.numberOfReferral,
        referralPointsRewarded: data.referral.referralPointsRewarded,
        pointsRewards: data.referral.pointsRewards,
      },
      pointsLottery: {
        seasonPointsSpent: data.junk.pointsRewards + data.chest.pointsRewards + data.referral.pointsRewards,
        creditsRewards: 0, // Will be set from season results (lottery winnings)
        seasonNFT: 0, // Will be set from season results
      },
    };
  } catch (error) {
    console.error('[useSeasonStats] Error fetching season stats:', error);
    // Return zeros on error
    return {
      junk: {
        numberOfTokens: 0,
        recycledCredits: 0,
        pointsRewards: 0,
      },
      chest: {
        numberOfChestOpened: 0,
        creditsRewarded: 0,
        pointsRewards: 0,
      },
      referral: {
        numberOfReferral: 0,
        referralPointsRewarded: 0,
        pointsRewards: 0,
      },
      pointsLottery: {
        seasonPointsSpent: 0,
        creditsRewards: 0,
        seasonNFT: 0,
      },
    };
  }
}

export function useSeasonStats(seasonId: number) {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['seasonStats', seasonId],
    queryFn: () => fetchSeasonStats(seasonId),
    enabled: isAuthenticated && seasonId > 0,
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}
